import { Card, CardContent, Stack, Typography } from '@mui/material';
import SeoHead from '../components/SeoHead';
// TODO(@dvgup/branding migration): replace with `@dvgup/branding`'s
// runtime export once TODO-013 lands. The current fixture supplies
// the `share.*` slot consumed by `<SeoHead>` below.
import { brandingFixture } from '../lib/branding/branding-fixture';

/**
 * About page (task 9.3, public-website spec).
 *
 * `<AboutPage>` at route `/about` renders the static "About DVGUP"
 * copy per **Requirement 2.7** (clicking `About` in the side nav
 * navigates to `/about` which renders a static page bundled with
 * the SPA).
 *
 * **Content source.** The eventual content per the requirement is a
 * static MDX file bundled with the SPA. That MDX bundling is
 * deferred until **TODO-029** (public-website tagline + description)
 * lands the real long-form copy — adding the `vite-plugin-mdx` /
 * `@mdx-js/rollup` dependency before the content exists would
 * commit the bundler shape to a transient placeholder. Until then,
 * the page reads plain text from
 * `BrandingConfig.copy.about` (added as an optional `about?: string`
 * slot on the `copy` interface for this purpose), which is the same
 * observable surface — paragraphs separated by blank lines, no
 * inline markup. When TODO-029 lands and `vite-plugin-mdx` is
 * wired, this component swaps the `brandingFixture.copy.about`
 * read for an `import AboutMdx from './about.mdx'` and renders
 * `<AboutMdx>`; the route, page chrome, and call-site stay
 * unchanged.
 *
 * **Fallback.** When `copy.about` is absent or empty (the
 * pre-TODO-029 state on a fresh `BrandingConfig`), the page falls
 * back to `copy.tagline` so the route always renders at least one
 * paragraph. This matches the "TODO-029 fallback" note in the
 * task description and mirrors the same graceful-omission posture
 * `meta-builder.ts` (task 10.1) takes for the share defaults.
 *
 * **Paragraph splitting.** The placeholder string MAY contain
 * blank-line-separated paragraphs (`\n\n`). The page splits on the
 * blank-line boundary, drops empty fragments, and renders one MUI
 * `<Typography variant="body1">` per paragraph. This keeps the
 * shape compatible with the eventual MDX content (which renders
 * one `<p>` per paragraph as well) so visitors see the same
 * vertical rhythm pre- and post-TODO-029.
 *
 * _Validates: Requirement 2.7 (static `/about` page bundled with
 * the SPA), R8.7 (branding-token sourcing via `BrandingConfig`)._
 */
export default function AboutPage(): JSX.Element {
  const aboutCopy = brandingFixture.copy.about;
  const fallbackCopy = brandingFixture.copy.tagline;
  const source =
    aboutCopy !== undefined && aboutCopy.trim() !== '' ? aboutCopy : fallbackCopy;
  const paragraphs = source
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  return (
    <Card sx={{ maxWidth: 720, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/about"
          pageTitle="About"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
          About DVGUP
        </Typography>
        <Stack spacing={2}>
          {paragraphs.map((paragraph, index) => (
            <Typography key={index} variant="body1">
              {paragraph}
            </Typography>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}
